/** @module Auth0MfaChoicePageHelper */
/* eslint-disable import/extensions */
import { Auth0PageBase } from './auth0-page-template.mst.mjs';
import Auth0Cdn from '../utils/auth0-cdn-resource-resolver.mst.mjs';
import MfaChooseChallengeHtml from '../../../public/mfaChooseChallenge.html.mst';
import Auth0MfaCodePageHelper from './auth0-mfa-code-page-helper.mst.mjs';
import Auth0Constants from '../../utils/auth0-constants.mjs';
import Auth0Localizer from '../../utils/auth0-localization-helper.mjs';
import Auth0UtilsBrowser from '../../utils/auth0-utils-browser.mjs';
import AuthAnalytics from '../../utils/auth-analytics.mjs';
import {
  STEP_CHOOSE_MFA_FACTOR_PAGE,
  EVENT_MFA_FACTOR_SELECTED,
  EVENT_MFA_CANCEL,
} from '../../utils/auth-analytics-constants.mjs';

const getEnrollmentName = (enrollment) => {
  if (enrollment.oob_channel === Auth0Constants.MFA_OOB_CHANNEL_EMAIL) {
    return Auth0Localizer.getLocalizedText('mfa-choice-email');
  }

  if (enrollment.oob_channel === Auth0Constants.MFA_OOB_CHANNEL_SMS) {
    return Auth0Localizer.getLocalizedText('mfa-choice-sms');
  }

  if (enrollment.oob_channel === Auth0Constants.MFA_OOB_CHANNEL_VOICE) {
    return Auth0Localizer.getLocalizedText('mfa-choice-voice');
  }

  if (enrollment.type === Auth0Constants.MFA_TYPE_TOTP) {
    return Auth0Localizer.getLocalizedText('mfa-choice-authenticator-app');
  }
};

const getEnrollmentType = (enrollment) => {
  if (enrollment.type === Auth0Constants.MFA_TYPE_TOTP) {
    return Auth0Constants.MFA_TYPE_TOTP;
  }
  return enrollment.oob_channel;
};

class Auth0MfaChoicePageHelper extends Auth0PageBase {
  constructor(options) {
    super(options);
    this.mfaToken = options.mfaGrantToken;
    this._uriState = options.mfaState;
    this._whiteLabelOptions = options.whiteLabelOptions;

    this._pageStyles = [
      `${Auth0Cdn.AUTH0_STYLES_URL}/auth0-mfa-styles.css?cb=71c5d67`,
    ];

    this._options = options;
    this._onFactorSelected = options.onFactorSelected;
  }

  get _mfaEnrollmentsWrapper() {
    return document.getElementById('mfa-enrollments-wrapper');
  }

  _buildMfaSegmentContext = () => {
    return AuthAnalytics.buildSigninContext({
      step: STEP_CHOOSE_MFA_FACTOR_PAGE,
      client_id: this._options.clientId,
      tenant: this.config?.auth0Tenant,
    });
  };

  _trackMfaEvent = (segmentEventId, enrollment, additionalProperties = {}) => {
    const _getGrantTokenUserId = () => Auth0UtilsBrowser.getJwtBodyFromToken(this.mfaToken)?.userId;
    const _getGrantTokenUserEmail = () => Auth0UtilsBrowser.getJwtBodyFromToken(this.mfaToken)?.email;
    const _getGrantTokenAmfaFactorId = () => Auth0UtilsBrowser.getJwtBodyFromToken(this.mfaToken)?.amfaFactorId;

    const properties = {
      email: _getGrantTokenUserEmail(),
      user_id: _getGrantTokenUserId(),
      factor_type: getEnrollmentType(enrollment),
      factor_name: enrollment.name,
      factor_id: _getGrantTokenAmfaFactorId(),
      ...additionalProperties,
    };
    AuthAnalytics.track(segmentEventId, properties, this._buildMfaSegmentContext());
  };

  _registerMfaChoiceAnalytics = () => {
    // Register tracking for each enrollment button
    this.enrollments.forEach((enrollment) => {
      const element = document.getElementById(enrollment.id);
      AuthAnalytics.registerButtonClickEvent(element, () => {
        this._trackMfaEvent(EVENT_MFA_FACTOR_SELECTED, enrollment);
      });
    });

    // Cancel/logout link tracking (EVENT_MFA_CANCEL)
    // Uses inline await pattern before navigation in _cancelMfaAndLogout() handler
  };

  _cancelMfaAndLogout = async () => {
    // Track cancel event before logout/navigation
    await this._trackMfaEvent(EVENT_MFA_CANCEL, {});
    this._logout();
  };

  _onSendChallenge = async (challengeOptions, enrollment) => {
    const options = {
      challengeType: enrollment.type === Auth0Constants.MFA_TYPE_TOTP ? Auth0Constants.MFA_TYPE_OTP : Auth0Constants.MFA_TYPE_OOB,
      mfaGrantToken: challengeOptions.mfaGrantToken,
      state: challengeOptions.state,
      factor: enrollment,
    };
    const res = await this._auth0ManagementApi.challengeMfaEnrollmentForLogin(options);

    return {
      mfaToken: res.mfaToken,
      oobCode: res.oobCode,
      amfaContext: res.challengeContextToken,
    };
  };

  _getInjectedValuesByEnrollmentType(enrollment) {
    if (enrollment.type === Auth0Constants.MFA_TYPE_TOTP) {
      return {
        shouldHideSendCode: true,
      };
    }
    return {
      shouldHideSendCode: false,
      sentToValue: enrollment.name,
    };
  }

  _onEnrollmentClick = async (enrollment) => {
    if (this._isChoiceInFlight) {
      return;
    }
    this._isChoiceInFlight = true;

    const spinnerMessage = enrollment.type === Auth0Constants.MFA_TYPE_TOTP
      ? undefined
      : 'mfa-please-wait';
    this.showSpinner(spinnerMessage);

    if (this._onFactorSelected) {
      this._onFactorSelected(enrollment);
      return;
    }

    new Auth0MfaCodePageHelper({
      mfaFactorType: getEnrollmentType(enrollment),
      mfaGrantToken: this.mfaToken,
      mfaState: this._uriState,
      name: 'Auth0MfaChoicePageHelper',
      tenantOrDomain: this._options.tenantOrDomain,
      whiteLabelOptions: this._options.whiteLabelOptions,
      appLoginUrl: this._options.appLoginUrl,
      clientId: this._options.clientId,
      shouldRenderRememberMe: true,
      sendVerificationCode: challengeOptions => this._onSendChallenge(challengeOptions, enrollment),
      onGoBack: () => {
        this._isChoiceInFlight = false;
        this.render();
      },
      ...this._getInjectedValuesByEnrollmentType(enrollment),
    }).render();
  };

  _showCodedMfaInitError = (code) => {
    this._displayError(
      'error-mfa-initialization',
      {
        code,
        supportEmail: this._whiteLabelHelper.appLoginSupportEmail,
        supportPhoneNumber: this._whiteLabelHelper.appLoginSupportPhoneNumber,
      },
    );
  };

  async _onLoad() {
    await super._onLoad();
    this.toolbarVisible = true;
    this.actionLinkVisible = true;
    this.actionLinkText = Auth0Localizer.getLocalizedText('logout-link-text');
    this.actionLinkTextHandler = this._cancelMfaAndLogout;

    console.log('mfa choice: _onLoad');
    if (!this.enrollments) {
      try {
        const resData = await this._auth0ManagementApi.getMfaEnrollmentsForLogin({ mfaGrantToken: this.mfaToken });
        this.enrollments = resData;
      } catch (error) {
        this._showCodedMfaInitError(error.code);
      }
    }

    console.log('SET HTML');
    this.setBody(MfaChooseChallengeHtml);
  }

  _localize = async () => {
    await super._localize();
    Auth0Localizer.setElementLocalizedText({ id: 'mfa-choice-title', resourceIdOverride: 'mfa-choice-title' });
    Auth0Localizer.setElementLocalizedText({ id: 'mfa-choice-preamble', resourceIdOverride: 'mfa-choice-preamble' });
  };

  _initMfaChoiceHtml = async () => {
    await this._localize();
    await this._applyWhiteLabel(this._whiteLabelOptions);

    if (!this.enrollments) {
      return;
    }

    this._mfaEnrollmentsWrapper.innerHTML = this.enrollments
      .map((enrollment) => {
        return `<div class="mfa-enrollment-option" id="${enrollment.id}" tabindex="0"><span class="mfa-enrollment-label">${getEnrollmentName(enrollment)}</span><span>${enrollment.name}<span></div>`;
      })
      .join('');

    this.enrollments.forEach((enrollment) => {
      const element = document.getElementById(enrollment.id);
      element.addEventListener('click', () => {
        this._onEnrollmentClick(enrollment);
      });
    });

    // Register all Segment analytics tracking
    this._registerMfaChoiceAnalytics();
  };

  /**
   * @function
   * @async
   */
  async _startUi() {
    console.log('mfa choice: _startUi');
    await super._startUi();
    await this._initMfaChoiceHtml();
    this._showPage();
  }

  render(forceRerender = false) {
    if (this._pageWasLoadedViaRefresh) {
      console.log('mfa: page was loaded via refresh, override refresh; navigate to =>', this._appLoginUrl);
      // special handling for page refresh. This page loads with a signed jwt issued by auth0 actions and
      // it expires in 10 minutes. If the user refreshes the page, they are still in a partially authenticated
      // state and we want to redirect user back through auth0 action to get a new jwt (MFA Grant) and then
      // reload this page with the new jwt. This will do two things: 1) ensure user always has a valid jwt even if
      // they have been at the code page longer than 10 minutes (support issues etc) and 2.) ensure that the user
      // picks up any email address changes immediately based on working with support to change their email address
      window.location = this._appLoginUrl;
      return;
    }
    super.render(forceRerender);
  }
}

export default Auth0MfaChoicePageHelper;
